import { useState } from 'react';
import { useDAWStore } from '../store/daw-store';
import type { Theme } from '../store/daw-store';
import { X, Save, Palette } from 'lucide-react';

interface ThemeEditorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ThemeEditor({ isOpen, onClose }: ThemeEditorProps) {
  const { currentTheme, addCustomTheme, setTheme } = useDAWStore();
  
  // Initialize with current theme colors
  const [themeName, setThemeName] = useState('My Custom Theme');
  const [colors, setColors] = useState(currentTheme.colors);

  if (!isOpen) return null;

  const handleColorChange = (key: keyof typeof colors, value: string) => {
    const newColors = { ...colors, [key]: value };
    setColors(newColors);
    
    // Real-time preview: apply colors to CSS variables
    const root = document.documentElement;
    const colorMap: Record<string, string> = {
      primary: '--daw-bg-primary',
      secondary: '--daw-bg-secondary',
      tertiary: '--daw-bg-tertiary',
      elevated: '--daw-bg-elevated',
      border: '--daw-border',
      borderLight: '--daw-border-light',
      accentPrimary: '--daw-accent-primary',
      accentHover: '--daw-accent-hover',
      accentActive: '--daw-accent-active',
    };
    
    const cssVar = colorMap[key];
    if (cssVar) {
      root.style.setProperty(cssVar, value);
    }
  };

  const handleSave = () => {
    const newTheme: Theme = {
      id: `custom-${Date.now()}`,
      name: themeName,
      colors: colors,
    };
    
    addCustomTheme(newTheme);
    setTheme(newTheme);
    onClose();
  };

  const handleCancel = () => {
    // Restore original theme
    setTheme(currentTheme);
    onClose();
  };

  const colorFields: Array<{ key: keyof typeof colors; label: string; description: string }> = [
    { key: 'primary', label: 'Primary Background', description: 'Main background color' },
    { key: 'secondary', label: 'Secondary Background', description: 'Secondary panels' },
    { key: 'tertiary', label: 'Tertiary Background', description: 'Nested elements' },
    { key: 'elevated', label: 'Elevated Background', description: 'Modals and popups' },
    { key: 'border', label: 'Border Color', description: 'Default borders' },
    { key: 'borderLight', label: 'Light Border', description: 'Subtle dividers' },
    { key: 'accentPrimary', label: 'Primary Accent', description: 'Main accent color' },
    { key: 'accentHover', label: 'Hover Accent', description: 'Hover state' },
    { key: 'accentActive', label: 'Active Accent', description: 'Active/pressed state' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-[var(--daw-bg-elevated)] border border-[var(--daw-border)] rounded-lg shadow-2xl w-[900px] max-h-[700px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--daw-border)]">
          <div className="flex items-center gap-3">
            <Palette className="w-5 h-5 text-[var(--daw-accent-primary)]" />
            <h2 className="text-lg font-semibold text-white">Create Custom Theme</h2>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-[var(--daw-bg-tertiary)] rounded transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Theme Name Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Theme Name
            </label>
            <input
              type="text"
              value={themeName}
              onChange={(e) => setThemeName(e.target.value)}
              className="w-full px-4 py-2 bg-[var(--daw-bg-secondary)] border border-[var(--daw-border)] rounded text-white focus:outline-none focus:border-[var(--daw-accent-primary)] transition-colors"
              placeholder="Enter theme name"
            />
          </div>

          {/* Color Pickers Grid */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Color Variables</h3>
            
            {colorFields.map(({ key, label, description }) => (
              <div key={key} className="flex items-center gap-4 p-3 bg-[var(--daw-bg-secondary)] rounded border border-[var(--daw-border)]">
                {/* Color Preview & Picker */}
                <div className="relative">
                  <input
                    type="color"
                    value={colors[key]}
                    onChange={(e) => handleColorChange(key, e.target.value)}
                    className="w-16 h-16 rounded cursor-pointer border-2 border-[var(--daw-border-light)]"
                    style={{ backgroundColor: colors[key] }}
                  />
                </div>

                {/* Label & Description */}
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{label}</div>
                  <div className="text-xs text-gray-400">{description}</div>
                </div>

                {/* Hex Input */}
                <input
                  type="text"
                  value={colors[key]}
                  onChange={(e) => handleColorChange(key, e.target.value)}
                  className="w-28 px-3 py-1.5 bg-[var(--daw-bg-tertiary)] border border-[var(--daw-border)] rounded text-white text-sm font-mono focus:outline-none focus:border-[var(--daw-accent-primary)] transition-colors"
                  placeholder="#000000"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
            ))}
          </div>

          {/* Preview Section */}
          <div className="mt-6 p-4 bg-[var(--daw-bg-secondary)] rounded border border-[var(--daw-border)]">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">Live Preview</h3>
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1 h-12 rounded border border-[var(--daw-border)]" style={{ backgroundColor: colors.primary }} />
                <div className="flex-1 h-12 rounded border border-[var(--daw-border)]" style={{ backgroundColor: colors.secondary }} />
                <div className="flex-1 h-12 rounded border border-[var(--daw-border)]" style={{ backgroundColor: colors.tertiary }} />
              </div>
              <div className="flex gap-2">
                <div className="flex-1 h-12 rounded border border-[var(--daw-border)]" style={{ backgroundColor: colors.accentPrimary }} />
                <div className="flex-1 h-12 rounded border border-[var(--daw-border)]" style={{ backgroundColor: colors.accentHover }} />
                <div className="flex-1 h-12 rounded border border-[var(--daw-border)]" style={{ backgroundColor: colors.accentActive }} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--daw-border)] bg-[var(--daw-bg-secondary)]">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-[var(--daw-bg-tertiary)] rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-[var(--daw-accent-primary)] hover:bg-[var(--daw-accent-hover)] text-white rounded transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Theme
          </button>
        </div>
      </div>
    </div>
  );
}
